/**
 * ServiceBundlePicker — "Add bundle" for the fixed parts of a service DEFINITION (#3845, epic #3841; reconciled with #3034).
 *
 * A service links a part bundle as ONE row and shows it straight away; the bundle expands into part lines only when the
 * service is copied to a work order or quote. So this picker reads the bundle's expansion only to learn its id, name and
 * line count and hands that to the caller. It has no work order, resolves no price, books nothing, touches no stock and
 * shows no "rows were added" toast (there are no rows). The work-order picker (`workorders/PartBundlePicker`) is a different
 * component on purpose: it REQUIRES a work order and prices every part server-side, and must never become optional.
 *
 * Rights: listing and reading the expansion need `inventory.read`; without it nothing is rendered or requested.
 *
 * Stale results: one selection belongs to the owner (tenant, user, session family, generation, rights) it started under.
 * The expansion read, its success AND its failure are fenced against owner change, lock, loss of the right, unmount and a
 * newer selection, so a late answer of a previous owner never reaches `onInsert`, the screen or a notification. The picker
 * is keyed by owner, so another owner gets a fresh dialog in the same render. One selection runs at a time.
 *
 * Bundle strings live in the `inventory` namespace (`bundles.picker.*`).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { unavailablePartNames } from '@/components/workorders/unavailableParts';
import { useDebounce } from '@/hooks/useDebounce';
import { useNotify } from '@/hooks/useNotify';
import {
  fetchPartBundleExpansion,
  usePartBundleOptions,
  type PartBundleExpansion,
  type PartBundleListItem,
} from '@/hooks/useInventory';
import { describeApiError } from '@/lib/describeApiError';
import {
  PRICING_READ_PERMISSION,
  accessKey,
  OwnerChangedError,
  fenceHolds,
  isSessionCancel,
  takeFence,
  usePricingAccess,
  type PricingAccess,
  type PricingFence,
} from '@/features/inventory/pricing/pricingSession';

/** What reading a bundle's expansion needs. Deliberately not a pricing or work-order right. */
const BUNDLE_READ_PERMISSIONS: readonly string[] = [PRICING_READ_PERMISSION];

export interface ServiceBundlePickerProps {
  /** Receives the chosen bundle's expansion; the caller links the bundle (by `bundleId`) to the service draft. */
  onInsert: (expansion: PartBundleExpansion) => void;
  disabled?: boolean;
}

/** The public picker: the owner and rights are its identity. Any change remounts the inner picker in that render. */
export function ServiceBundlePicker(props: ServiceBundlePickerProps) {
  const access = usePricingAccess();
  return <ServiceBundlePickerInner key={accessKey(access)} access={access} {...props} />;
}

function ServiceBundlePickerInner({
  access,
  onInsert,
  disabled = false,
}: ServiceBundlePickerProps & { access: PricingAccess }) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const canReadInventory = access.canRead;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [insertingId, setInsertingId] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const mountedRef = useRef(true);
  // One selection at a time, decided synchronously: `insertingId` is state and lags a second click of the same tick.
  const busyRef = useRef(false);
  // Identity of the running selection: only the selection that owns it may report or clear the busy state.
  const operationRef = useRef(0);
  const onInsertRef = useRef(onInsert);
  onInsertRef.current = onInsert;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // A selection still in flight belongs to a picker that is gone (other owner or unmount).
      operationRef.current += 1;
    };
  }, []);

  const query = useDebounce(search.trim(), 250);
  const bundles = usePartBundleOptions(query ? { q: query } : {}, { enabled: open && canReadInventory });

  if (!canReadInventory) return null;

  const close = () => {
    if (insertingId) return;
    setOpen(false);
    setSearch('');
    setRefusal(null);
  };

  const choose = async (bundle: PartBundleListItem) => {
    if (busyRef.current || insertingId) return;
    busyRef.current = true;
    const operation = ++operationRef.current;
    let fence: PricingFence | null = null;
    // Still the selection that asked: mounted, not superseded, and the session, user, tenant, generation, lock and right it
    // began under are still the live ones. Checked before anything the chain produced is used.
    const current = () =>
      mountedRef.current && operationRef.current === operation && fence !== null && fenceHolds(fence);
    setInsertingId(bundle.id);
    setRefusal(null);
    try {
      try {
        fence = takeFence(access, BUNDLE_READ_PERMISSIONS);
      } catch (error) {
        // Nothing was sent. A session that moved on since this render shows nothing; a right lost meanwhile is a refusal.
        if (error instanceof OwnerChangedError) return;
        throw error;
      }
      const expansion = await fetchPartBundleExpansion(bundle.id);
      if (!current()) return;
      onInsertRef.current(expansion);
      setOpen(false);
      setSearch('');
    } catch (error) {
      // The owner, the right or the picker changed under the chain: the error concerns a screen that is gone, and its content
      // (part names, reasons) is not read, shown or notified.
      if (isSessionCancel(error)) return;
      if (fence !== null ? !current() : !mountedRef.current || operationRef.current !== operation) return;
      const names = unavailablePartNames(error);
      const message =
        names !== null
          ? t('bundles.picker.unavailable', { name: bundle.name, parts: names.join(', ') })
          : describeApiError(error, tc, { fallback: t('bundles.picker.failed') });
      setRefusal(message);
      notify.error(tc('error'), message);
    } finally {
      // Only the selection that owns the busy state clears it, so a stale completion can never release a newer action.
      if (operationRef.current === operation) {
        busyRef.current = false;
        if (mountedRef.current) setInsertingId(null);
      }
    }
  };

  const rows = bundles.data?.data ?? [];

  return (
    <>
      <Tooltip title={t('bundles.picker.button')}>
        <span>
          <Button
            size="small"
            startIcon={<InventoryIcon fontSize="small" />}
            endIcon={<AddIcon fontSize="small" />}
            onClick={() => setOpen(true)}
            disabled={disabled}
            aria-label={t('bundles.picker.button')}
          >
            {t('bundles.picker.button')}
          </Button>
        </span>
      </Tooltip>

      <ResponsiveDialog
        open={open}
        onClose={close}
        maxWidth="xs"
        fullWidth
        mobileTitle={t('bundles.picker.title')}
        aria-labelledby="service-bundle-picker-title"
      >
        <DialogTitle id="service-bundle-picker-title">{t('bundles.picker.title')}</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={t('bundles.picker.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ mb: 1, mt: 0.5 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              },
              htmlInput: { 'aria-label': t('bundles.picker.searchPlaceholder') },
            }}
          />
          {refusal && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {refusal}
            </Alert>
          )}
          {bundles.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} aria-label={tc('loading')} />
            </Box>
          ) : bundles.isError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => void bundles.refetch()}>
                  {t('bundles.picker.retry')}
                </Button>
              }
            >
              {t('bundles.picker.loadFailed')}
            </Alert>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>
              {t('bundles.picker.empty')}
            </Typography>
          ) : (
            <List dense disablePadding aria-label={t('bundles.picker.title')}>
              {rows.map((bundle) => (
                <ListItemButton
                  key={bundle.id}
                  onClick={() => void choose(bundle)}
                  disabled={insertingId !== null}
                  aria-label={bundle.name}
                >
                  <ListItemText
                    primary={bundle.name}
                    secondary={
                      [
                        bundle.description,
                        t('bundles.picker.partCount', { count: bundle.itemCount }),
                        bundle.unavailableCount > 0 ? t('bundles.picker.unavailableBadge') : null,
                      ]
                        .filter((part): part is string => typeof part === 'string' && part !== '')
                        .join(' · ')
                    }
                  />
                  {insertingId === bundle.id && <CircularProgress size={18} aria-label={t('bundles.picker.inserting')} />}
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={insertingId !== null}>
            {tc('close')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  );
}
